import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import uniq from 'lodash-es/uniq';
import { useMemo } from 'react';
import { useClient } from '../../ClientProvider';
import { allOrgRole } from '../../enums';
import { mergeQueryState } from '../../mergeState';
import { isTargetsWithTZ, plotsOnlySources, sourceToMetric } from '../../resources/alerts.types';
import useQuery from '../../useQuery';
const EMPTY_ARRAY = [];
const EMPTY_PLOT_ARRAY = [];
const EMPTY_DEVICE_ARRAY = [];
const EMPTY_STATION_ARRAY = [];
const useGetCompleteAlertTargets = (targets, sources, fetchOrg = true) => {
    const client = useClient();
    const plotIds = targets === undefined
        ? EMPTY_ARRAY
        : !isTargetsWithTZ(targets)
            ? targets.plots.map((id) => id)
            : targets.plots.map(({ id }) => id);
    const deviceIds = targets === undefined
        ? EMPTY_ARRAY
        : !isTargetsWithTZ(targets)
            ? targets.devices.map((id) => id)
            : targets.devices.map(({ id }) => id);
    const stationIds = targets === undefined
        ? EMPTY_ARRAY
        : !isTargetsWithTZ(targets)
            ? targets.stations.map((id) => id)
            : targets.stations.map(({ id }) => id);
    const hasPlotOnlySource = !isEmpty(sources) && sources.some((s) => plotsOnlySources.includes(s));
    const sourcesAsMetrics = useMemo(() => sources.map((s) => sourceToMetric(s)), [sources]);
    const plotsQueryParams = useMemo(() => ({ isArchived: false, ids: plotIds }), [plotIds]);
    const devicesQueryParams = useMemo(() => ({ ids: deviceIds }), [deviceIds]);
    const stationsQueryParams = useMemo(() => ({ ids: stationIds }), [stationIds]);
    const plotsRequest = useQuery(client.plots.getAllPages(plotsQueryParams), {
        enabled: !isEmpty(plotIds)
    });
    // Not disabling when there is a plot only source, as the sources might have changed
    // between when the target was added and this fetch so we still need the device details
    // to check the compatibility
    const devicesRequest = useQuery(client.devices.getAll(devicesQueryParams), {
        enabled: !isEmpty(deviceIds)
    });
    const stationsRequest = useQuery(client.stations.getAll(stationsQueryParams), {
        enabled: !isEmpty(stationIds)
    });
    /** testing for isEmpty to avoid cache taking over plots & devices */
    const plots = isEmpty(plotIds) ? EMPTY_PLOT_ARRAY : plotsRequest.data;
    const devices = isEmpty(deviceIds) ? EMPTY_DEVICE_ARRAY : devicesRequest.data;
    const stations = isEmpty(stationIds) ? EMPTY_STATION_ARRAY : stationsRequest.data;
    const plotsOrgIds = useMemo(() => plots ? plots?.filter((p) => !isNil(p.organizationId)).map((p) => p.organizationId) : [], [plots]);
    const devicesOrgIds = useMemo(() => devices ? devices?.filter((d) => !isNil(d.organizationId)).map((d) => d.organizationId) : [], [devices]);
    const stationOrgIds = useMemo(() => stations
        ? stations?.filter((d) => !isNil(d.organizationId)).map((d) => d.organizationId)
        : [], [stations]);
    const allOrgIds = plots ? uniq([...plotsOrgIds, ...devicesOrgIds, ...stationOrgIds]) : EMPTY_ARRAY;
    const orgsQueryParams = useMemo(() => ({ ids: allOrgIds, myRole: allOrgRole }), [allOrgIds]);
    const orgsRequest = useQuery(client.orgs.getMany(orgsQueryParams), {
        enabled: !isEmpty(allOrgIds) && fetchOrg
    });
    const orgs = useMemo(() => orgsRequest.data?.results, [orgsRequest.data]);
    const completePlots = useMemo(() => plots
        ? plots.map((plt) => {
            const matchingOrg = orgs?.find((org) => org.id === plt.organizationId);
            // if its measures do not include one of the sources
            const hasWarning = !isEmpty(sourcesAsMetrics)
                ? sourcesAsMetrics.some((src) => !plt.availableMeasures.includes(src))
                : false;
            const ntwkId = matchingOrg?.subscribedNetworkId;
            const result = {
                ...plt,
                orgName: matchingOrg?.name,
                orgNtwkId: !isNil(ntwkId) ? ntwkId : undefined,
                hasWarning
            };
            return result;
        })
        : [], [plots, sourcesAsMetrics, orgs]);
    const completeDevices = useMemo(() => devices
        ? devices.map((dvc) => {
            const matchingOrg = orgs?.find((org) => org.id === dvc.organizationId);
            // if its measures do not include one of the sources or a plot only sources
            // is selected for a device
            const hasWarning = !isEmpty(sourcesAsMetrics)
                ? sourcesAsMetrics.some((src) => !dvc.availableMeasures.includes(src)) ||
                    hasPlotOnlySource
                : false;
            const ntwkId = matchingOrg?.subscribedNetworkId;
            const result = {
                ...dvc,
                orgName: matchingOrg?.name,
                orgNtwkId: !isNil(ntwkId) ? ntwkId : undefined,
                hasWarning
            };
            return result;
        })
        : [], [devices, sourcesAsMetrics, orgs, hasPlotOnlySource]);
    const completeStations = useMemo(() => stations
        ? stations.map((station) => {
            const matchingOrg = orgs?.find((org) => org.id === station.organizationId);
            // if its measures do not include one of the sources or a plot only sources
            // is selected for a device
            const hasWarning = !isEmpty(sourcesAsMetrics)
                ? sourcesAsMetrics.some((src) => !station.availableMeasures.includes(src)) ||
                    hasPlotOnlySource
                : false;
            const ntwkId = matchingOrg?.subscribedNetworkId;
            const result = {
                ...station,
                orgName: matchingOrg?.name,
                orgNtwkId: !isNil(ntwkId) ? ntwkId : undefined,
                hasWarning
            };
            return result;
        })
        : [], [stations, sourcesAsMetrics, orgs, hasPlotOnlySource]);
    const completeTargets = useMemo(() => [...completePlots, ...completeDevices, ...completeStations], [completePlots, completeDevices, completeStations]);
    const hasWarnings = useMemo(() => (!isEmpty(completeTargets) ? completeTargets.some((t) => t.hasWarning) : false), [completeTargets]);
    const { isInitialLoading: isLoading } = mergeQueryState(plotsRequest, devicesRequest, orgsRequest);
    return {
        completeTargets,
        hasWarnings,
        isLoading
    };
};
export default useGetCompleteAlertTargets;
